/* eslint-disable no-constant-binary-expression */
/* eslint-disable no-case-declarations */
import { useContext } from "react"
import { GanttContext, getDateAsIndex, getIndexAsDate, getIndexInRange, getIsInRange, getMonthDateRange, getPositionFromEndOfRange, getPositionInRange, getRangeOverlap, getWeekDateRange } from "./Gantt"
import { isWorkingDay } from "@/utils/GlobalConstants"

interface GanttObjProps {
    color: string
    startDate: Date
    finishDate: Date
    completedDate?: Date
}

const GanttObj = ({ startDate, finishDate, color, completedDate }: GanttObjProps) => {
    const ganttProps = useContext(GanttContext);

    function getObjectByScale(index: number): JSX.Element {
        switch (ganttProps.ganttScale) {
            case "month":
                const monthRange = getMonthDateRange(index, ganttProps.displayStartDate);
                return < GanttObjMonth color={color} startDate={startDate} finishDate={finishDate} completedDate={completedDate} scaleStartDate={monthRange.startDate} scaleFinishDate={monthRange.finishDate} />;
            case "week":
                const weekRange = getWeekDateRange(index, ganttProps.displayStartDate);
                return <GanttObjWeek color={color} startDate={startDate} finishDate={finishDate} completedDate={completedDate} scaleStartDate={weekRange.startDate} scaleFinishDate={weekRange.finishDate} />;
            case "day":
                return <GanttObjDay color={color} startDate={startDate} finishDate={finishDate} completedDate={completedDate} index={index} />;
            case "year":
                const yearStartDate = new Date(ganttProps.displayStartDate);
                yearStartDate.setMonth(0, 1); // Set to January 1st of the year
                yearStartDate.setFullYear(yearStartDate.getFullYear() + index);
                const yearFinishDate = new Date(yearStartDate);
                yearFinishDate.setFullYear(yearFinishDate.getFullYear() + 1);
                return <GanttObjYear color={color} startDate={startDate} finishDate={finishDate} completedDate={completedDate} scaleStartDate={yearStartDate} scaleFinishDate={yearFinishDate} />;
            default:
                return <GanttObjDay color={color} startDate={startDate} finishDate={finishDate} index={index} />; // Default to day if scale is not recognized        
        }
    }

    return (
        <div className="flex items-center h-10 w-full">
            <div className="w-full h-full bg-ganttchart-background relative">
                <div className="absolute top-0 left-0 h-full w-full flex">
                    {Array.from({ length: ganttProps.displayLength }).map((_, index) => (
                        <div
                            key={index}
                            className={"h-full border-r " + ((ganttProps.ganttScale === "day" && getDateAsIndex(new Date(), ganttProps.displayStartDate) === index) ? "border-ganttchart-current-date-line" :  "border-ganttchart-gridline")
                            }
                            style={{
                                width: `${100 / ganttProps.displayLength}%`,
                            }}
                        >
                            {getObjectByScale(index)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default GanttObj

interface GanttObjScaleProps extends GanttObjProps {
    index?: number
    completedDate?: Date
}

interface GanttObjScaleRangeProps extends GanttObjScaleProps {
    scaleStartDate: Date
    scaleFinishDate: Date
}

const GanttYearObj = ({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }: GanttObjScaleRangeProps) => {
    const colWidth = 100 / 365; // Assuming 365 days in a year for simplicity

    return (
        GanttScaleRangeObject({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }, colWidth)
    )
}
export const GanttObjYear = GanttYearObj;

const GanttMonthObj = ({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }: GanttObjScaleRangeProps) => {
    const daysInMonth = new Date(scaleStartDate.getFullYear(), scaleStartDate.getMonth() + 1, 0).getDate();
    const colWidth = 100 / daysInMonth;

    return (
        GanttScaleRangeObject({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }, colWidth)
    )
}
export const GanttObjMonth = GanttMonthObj;

const GanttWeekObj = ({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }: GanttObjScaleRangeProps) => {
    const colWidth = 100 / 7; // Assuming 7 days in a week
    return (
        GanttScaleRangeObject({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }, colWidth)
    )
}

export const GanttObjWeek = GanttWeekObj;

const GanttDayObj = ({ startDate, finishDate, completedDate, color, index }: GanttObjScaleProps) => {
    const ganttProps = useContext(GanttContext);

    return (
        <div className={"flex flex-col h-full py-2" + " " + (ganttProps.ganttScale === "day" && isWorkingDay(getIndexAsDate(index!, ganttProps.displayStartDate)) ? "bg-ganttchart-background" : "bg-ganttchart-background brightness-50")}>
            {((getDateAsIndex(new Date(startDate), ganttProps.displayStartDate) == index) || (getDateAsIndex(new Date(finishDate), ganttProps.displayStartDate) == index)) ? (
                <>
                    <div className="relative flex h-full w-full items-center justify-center">
                        {/* Line behind the circle, aligned left or right */}
                        {new Date(startDate).toDateString() !== new Date(finishDate).toDateString() && getIndexInRange(index!, startDate, finishDate, ganttProps.displayStartDate) && (
                            <div
                                className={
                                    `absolute top-1/2 transform -translate-y-1/2 h-2 w-1/2 ${color} ` +
                                    (getDateAsIndex(new Date(startDate), ganttProps.displayStartDate) == index
                                        ? "right-0"
                                        : "left-0")
                                }
                                style={{
                                    zIndex: 4,
                                }}
                            ></div>
                        )}
                        {/* Centered circle */}
                        <div
                            className={`relative z-5 h-4 w-4 rounded-full mx-auto ${completedDate &&
                                getDateAsIndex(new Date(completedDate), ganttProps.displayStartDate) === index
                                ? "bg-ganttchart-completed-task"
                                : color
                                }`}
                            style={{
                                zIndex: 5,
                            }}
                        ></div>
                    </div>
                </>
            ) : (completedDate &&
                getDateAsIndex(new Date(completedDate), ganttProps.displayStartDate) === index ? (
                <>
                    <div className="relative flex h-full w-full items-center justify-center">
                        {/* Line behind the circle, aligned left or right */}
                        {getIndexInRange(index!, startDate, finishDate, ganttProps.displayStartDate) && (
                            <div
                                className={
                                    `absolute top-1/2 transform -translate-y-1/2 h-2 w-full ${color}`
                                }
                                style={{
                                    zIndex: 4,
                                }}
                            ></div>
                        )}
                        {/* Centered circle */}
                        <div
                            className={`relative z-5 h-4 w-4 rounded-full mx-auto bg-ganttchart-completed-task`}
                            style={{
                                zIndex: 5,
                            }}
                        ></div>
                    </div>
                </>
            ) :
                getIndexInRange(index!, startDate, finishDate, ganttProps.displayStartDate) ? (
                    // If the index is within the range of startDate and finishDate draw the line
                    <div className={"flex h-2 w-full mx-auto my-auto " + color}></div>
                ) : null
            )}
        </div>
    )
}
export const GanttObjDay = GanttDayObj;

const GanttScaleRangeObject = ({ startDate, finishDate, completedDate, color, scaleStartDate, scaleFinishDate }: GanttObjScaleRangeProps, colWidth: number) => {
    const ganttProps = useContext(GanttContext);

    // if startDate and finishDate are the same, we can just draw a circle
    if (new Date(startDate).toDateString() === new Date(finishDate).toDateString()) {
        // If completedDate is provided, check if it matches the startDate, if so make the circle green
        const isCompletedOnStartDate = completedDate && new Date(completedDate).toDateString() === new Date(startDate).toDateString();
        return (
            <div className={"relative flex h-full"}>
                <div className="relative flex w-full h-full">
                    {getIsInRange(new Date(startDate), scaleStartDate, scaleFinishDate) && (
                        <div
                            className={"h-full flex items-center justify-center" + (new Date(startDate).toDateString() === new Date().toDateString() ? " border-r border-ganttchart-current-date-line" : "")}
                            style={{
                                width: `${colWidth}%`,
                                marginLeft: `${getPositionInRange(scaleStartDate, scaleFinishDate, new Date(startDate))}%`,
                            }}
                        >
                            <div className="flex items-center justify-center w-full h-full">
                                {GanttDateObj({ color: isCompletedOnStartDate ? "bg-ganttchart-completed-task" : color})}
                            </div>
                            {completedDate && !isCompletedOnStartDate && (
                                <GanttCompletedDateObj
                                    completedDate={completedDate}
                                    scaleStartDate={scaleStartDate}
                                    scaleFinishDate={scaleFinishDate}
                                    columnWidth={colWidth}
                                />
                            )}
                        </div>
                    )}
                </div>
                {new Date(startDate).toDateString() !== new Date().toDateString() && 
                new Date(finishDate).toDateString() !== new Date().toDateString() && 
                new Date(completedDate!).toDateString() !== new Date().toDateString() && (
                    <GanttDateLine
                        date={new Date()}
                        scaleStartDate={scaleStartDate}
                        scaleFinishDate={scaleFinishDate}
                        columnWidth={colWidth}
                    />
                )}
                {ganttProps.ganttScale === "year" && (
                    <GanttDividerLines numberOfLines={4} /> // display quarters
                )}
            </div>
        )
    }
    return (
        <div className="relative flex h-full">
            <div className="relative flex w-full h-full">
                {getIsInRange(new Date(startDate), scaleStartDate, scaleFinishDate) && (
                    <GanttStartFinishDateObj
                        color={color}
                        scaleStartDate={scaleStartDate}
                        scaleFinishDate={scaleFinishDate}
                        date={startDate}
                        completedDate={completedDate}
                        columnWidth={colWidth}
                        isStart={true}
                    />
                )}
                {getRangeOverlap(scaleStartDate, scaleFinishDate, new Date(startDate), new Date(finishDate)) && 
                 !(ganttProps.ganttScale === "year" && 
                   ((new Date(finishDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) <= 1) && ( // if the scale is year and the range is less than 1 day, we don't draw the line
                    <div className={"flex flex-grow h-2 my-auto " + color} style={{ position: "relative" }}></div>
                )}
                {getIsInRange(new Date(finishDate), scaleStartDate, scaleFinishDate) && (
                    <GanttStartFinishDateObj
                        color={color}
                        scaleStartDate={scaleStartDate}
                        scaleFinishDate={scaleFinishDate}
                        date={finishDate}
                        completedDate={completedDate}
                        columnWidth={colWidth}
                        isStart={false}
                    />
                )}
                {completedDate &&
                    (new Date(completedDate) !== new Date(startDate) && new Date(completedDate) !== new Date(finishDate)) && (
                        <GanttCompletedDateObj
                            completedDate={completedDate}
                            scaleStartDate={scaleStartDate}
                            scaleFinishDate={scaleFinishDate}
                            columnWidth={colWidth}
                        />
                    )
                }
            </div>
            {new Date(startDate).toDateString() !== new Date().toDateString() && 
                new Date(finishDate).toDateString() !== new Date().toDateString() && 
                new Date(completedDate!).toDateString() !== new Date().toDateString() && (
                    <GanttDateLine
                        date={new Date()}
                        scaleStartDate={scaleStartDate}
                        scaleFinishDate={scaleFinishDate}
                        columnWidth={colWidth}
                    />
                )}
                {ganttProps.ganttScale === "year" && (
                    <GanttDividerLines numberOfLines={4} /> // display quarters
                )}
        </div>
    )
}

interface GanttDateObjProps {
    color: string
    showDateLine?: boolean
}

const GanttDateObj = ({ color, showDateLine = false }: GanttDateObjProps) => {
    const borderStr = showDateLine ? " border-r border-ganttchart-current-date-line" : "";
    return (
        <div
            className={`relative z-5 h-4 w-4 rounded-full mx-auto ${color + borderStr
                }`}
            style={{
                zIndex: 5,
            }}
        ></div>
    )
}

interface GanttFinishDateObjProps extends GanttDateObjProps {
    scaleStartDate: Date
    scaleFinishDate: Date
    date: Date
    completedDate?: Date
    columnWidth: number
    isStart: boolean
}

const GanttStartFinishDateObj = ({ color, scaleStartDate, scaleFinishDate, date, completedDate, columnWidth, isStart }: GanttFinishDateObjProps) => {
    const borderStr = new Date().toDateString() === new Date(date).toDateString() ? "border-r border-ganttchart-current-date-line" : "";

    return (
        <div
            className={"h-full " + borderStr}
            style={{
                width: `${columnWidth}%`,
                marginRight: !isStart ? `${getPositionFromEndOfRange(scaleStartDate, scaleFinishDate, new Date(date))}%` : "0",
                marginLeft: isStart ? `${getPositionInRange(scaleStartDate, scaleFinishDate, new Date(date))}%` : "0",
            }}
        >
            <div className="relative flex h-full w-full items-center justify-center">
                {/* Line behind the circle, aligned left */}
                <div
                    className={
                        `absolute top-1/2 transform -translate-y-1/2 h-2 w-1/2 ${color} ${isStart ? "right-0" : "left-0"}`
                    }
                    style={{
                        zIndex: 4,
                    }}
                ></div>
                <GanttDateObj color={completedDate && new Date(date).toDateString() === new Date(completedDate).toDateString() ? "bg-ganttchart-completed-task" : color} />
            </div>
        </div>
    )
}

interface GanttCompletedDateObjProps {
    color?: string
    completedDate: Date
    scaleStartDate: Date
    scaleFinishDate: Date
    columnWidth: number
}

const GanttCompletedDateObj = ({ color = "bg-ganttchart-completed-task", completedDate, scaleStartDate, scaleFinishDate, columnWidth }: GanttCompletedDateObjProps) => {

    if (!getIsInRange(new Date(completedDate), scaleStartDate, scaleFinishDate)) {
        return null; // If the date is not in the range, do not render anything
    }

    const borderStr = new Date().toDateString() === new Date(completedDate).toDateString() ? "border-r border-ganttchart-current-date-line" : "";

    return (
        <div
            className={"absolute top-1/2 flex items-center justify-center " + borderStr}
            style={{
                transform: "translateY(-50%)",
                left: `${getPositionInRange(scaleStartDate, scaleFinishDate, new Date(completedDate))}%`,
                width: `${columnWidth}%`,
                height: "100%",
            }}
        >
            <div className={`h-4 w-4 rounded-full ${color} mx-auto z-6`}></div>
        </div>
    )
}

interface GanttDateLineObjProps {
    color?: string
    date: Date
    scaleStartDate: Date
    scaleFinishDate: Date
    columnWidth: number
}

const GanttDateLine = ({ color = "border-ganttchart-current-date-line", date, scaleStartDate, scaleFinishDate, columnWidth }: GanttDateLineObjProps) => {
    if (!getIsInRange(new Date(date), scaleStartDate, scaleFinishDate)) {
        return null; // If the date is not in the range, do not render anything
    }

    return (
        <div
            className={"absolute top-1/2 flex items-center justify-center border-r " + color}
            style={{
                transform: "translateY(-50%)",
                left: `${getPositionInRange(scaleStartDate, scaleFinishDate, new Date(date))}%`,
                width: `${columnWidth}%`,
                height: "100%",
            }}
        >
        </div>
    );
}

interface GanttDividerLinesProps {
    numberOfLines: number
}

const GanttDividerLines = ({ numberOfLines }: GanttDividerLinesProps) => {
    const lines = [];
    let position = 0;
    for (let i = 0; i < numberOfLines - 1; i++) {
        position = position + (100 / numberOfLines);
        lines.push(
            <div
                key={i}
                className="absolute top-0 h-full border-l border-ganttchart-gridline"
                style={{
                    left: `${position}%`,
                    width: `${100 / numberOfLines}%`,
                    zIndex: 5,
                }}
            ></div>
        );
    }
    return <>{lines}</>;
}